import { useEffect, useState } from "react";
import {
  FileText, Loader2, Save, AlertCircle, Plus, Trash2, Printer,
  ChevronDown, ChevronRight, CheckCircle2, AlertTriangle,
} from "lucide-react";
import {
  useUpdateChapterEventRequestMutation,
  type ChapterEventAppendage,
  type ChapterEventAppendageDay,
  type ChapterEventRequestDetail,
} from "../api/chapterEventsApi";

/**
 * The appendage to a request for CPD allocation — the Institute's template.
 *
 * The chapter does not simply ask for a number of points: it sets out what it
 * is holding, and the secretariat decides the points from that. This file is
 * the whole of that document in the portal — the chairman's form, the
 * secretariat's read-only copy, and the printable version — kept together on
 * purpose, because all three speak the same numbered sections (1.0, 1.1, 2.0,
 * then 2.N per day) and a numbering that drifted between the form and the
 * review screen would have the two sides discussing different paragraphs.
 *
 * The days are never typed. They come down from the API, one per day of the
 * event, so the appendage cannot describe a day the request does not cover.
 */

// ── the section headings, in one place ───────────────────────────────────────

const SECTIONS = {
  introduction: {
    no: "1.0",
    title: "Introduction",
    hint: "Introduce the programme — seminar, workshop, conference, technical training or any combination thereof. State the day, date, time and venue.",
  },
  aims_objectives: {
    no: "1.1",
    title: "Aim and Objectives of the Programme",
    hint: "State briefly the aims and objectives of the programme papers and training.",
  },
  about_theme: {
    no: "2.0",
    title: "About the Theme, Subthemes and Training Sessions",
    hint: "A brief, general description of the specific theme, the number and titles of papers to be presented, and the training sessions planned.",
  },
  other_information: {
    no: "",
    title: "Any other information",
    hint: "Anything else the secretariat should weigh that the sections above have no line for. Optional.",
  },
} as const;

/**
 * The numbered subsections under each day. The day's own summary is not one of
 * them — it sits directly under the "2.N Day N programme" heading, exactly as
 * it does on the paper template.
 */
const DAY_FIELDS = [
  {
    key: "sessions" as const,
    sub: ".1",
    title: "Theme / subthemes / training session planned",
    hint: "A brief summary of the papers for this day.",
    rows: 3,
  },
  {
    key: "technical_papers" as const,
    sub: ".2",
    title: "Technical papers",
    hint: "A detailed summary of the technicality in the papers, as it differs from normal presentations. Describe the training session model.",
    rows: 4,
  },
];

/** "2.1" for day 1, "2.2" for day 2 — the template's own numbering. */
const dayNo = (i: number) => `2.${i + 1}`;

const BLANK_PERSON = { topic: "", person: "", profile: "" };

export const EMPTY_APPENDAGE: ChapterEventAppendage = {
  introduction: "",
  aims_objectives: "",
  about_theme: "",
  days: [],
  other_information: "",
};

function fmtDay(iso: string, index: number) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return `Day ${index + 1}`;
  return d.toLocaleDateString("en-GB", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
}

function safeMsg(e: any): string {
  const errors = e?.data?.errors;
  if (errors) return Object.values(errors).flat().join(" ");
  return e?.data?.message || e?.error || e?.message || "Something went wrong.";
}

/** Whether a day has anything on it at all — what decides if it prints. */
function dayIsEmpty(d: ChapterEventAppendageDay) {
  return (
    !d.programme.trim() &&
    !d.sessions.trim() &&
    !d.technical_papers.trim() &&
    !d.resource_persons.some((p) => p.topic.trim() || p.person.trim() || p.profile.trim())
  );
}

// ── the chip the lists use ───────────────────────────────────────────────────

/**
 * Whether the appendage is there, said in one line.
 *
 * On the chapter's own queue it is a reminder; on the secretariat's tray it is
 * the difference between a request that can be judged and one that cannot. Same
 * component on both, so the two sides never describe the same request
 * differently.
 */
export function AppendageChip({ complete }: { complete?: boolean }) {
  if (complete) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-green-50 text-green-700 border border-green-200">
        <CheckCircle2 className="w-3 h-3" /> Appendage
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200">
      <AlertTriangle className="w-3 h-3" /> No appendage
    </span>
  );
}

// ── print ────────────────────────────────────────────────────────────────────

function esc(s: string) {
  return String(s ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Paragraphs, so a typed blank line survives into the printed document. */
function paras(s: string) {
  const text = (s ?? "").trim();
  if (!text) return `<p class="blank">—</p>`;
  return text
    .split(/\n{2,}/)
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br/>")}</p>`)
    .join("");
}

/**
 * The appendage as the Institute's paper form.
 *
 * Printed from the browser rather than rendered on the server: this is the same
 * document the screen is already showing, the portal has no Word writer on the
 * shared host, and "print to PDF" is what the secretariat actually does with it.
 */
export function printAppendage(req: ChapterEventRequestDetail) {
  const doc = req.appendage ?? EMPTY_APPENDAGE;

  const days = doc.days.map((day, i) => {
    const n = dayNo(i);

    // The form keeps a blank row at the foot of every table to type into, and
    // the API drops those on save. Printing straight from the form would put
    // them on the paper, so they come out here too.
    const people = day.resource_persons.filter(
      (p) => p.topic.trim() || p.person.trim() || p.profile.trim(),
    );

    const table = people.length
      ? `<table>
          <thead>
            <tr><th style="width:6%">S/N</th><th style="width:28%">Sub-Theme / Technical Paper</th>
            <th style="width:26%">Resource Persons</th><th>Profile of Resource Persons</th></tr>
          </thead>
          <tbody>
            ${people.map((p, idx) => `
              <tr>
                <td>${idx + 1}.</td>
                <td>${esc(p.topic)}</td>
                <td>${esc(p.person)}</td>
                <td>${esc(p.profile)}</td>
              </tr>`).join("")}
          </tbody>
        </table>`
      : `<p class="blank">—</p>`;

    return `
      <h2>${n} Day ${i + 1} programme <span class="date">(${esc(fmtDay(day.date, i))})</span></h2>
      ${paras(day.programme)}
      <h3>${n}.1 Theme / subthemes / training session planned</h3>
      ${paras(day.sessions)}
      <h3>${n}.2 Technical papers</h3>
      ${paras(day.technical_papers)}
      <h3>${n}.3 Resource people for keynote / subthemes / technical papers</h3>
      ${table}`;
  }).join("");

  const w = window.open("", "_blank", "width=1100,height=750");
  if (!w) return;

  w.document.write(`
    <html>
      <head>
        <title>Appendage — ${esc(req.title)}</title>
        <style>
          body { font-family: "Times New Roman", Times, serif; padding: 36px 48px; color: #000; line-height: 1.5; }
          h1 { font-size: 15pt; text-align: center; text-decoration: underline; margin: 0 0 22px; text-transform: uppercase; }
          h2 { font-size: 12pt; margin: 20px 0 6px; }
          h3 { font-size: 11pt; margin: 14px 0 4px; }
          p { font-size: 11pt; margin: 0 0 8px; text-align: justify; }
          p.blank { color: #777; }
          .meta { font-size: 10pt; border: 1px solid #000; padding: 10px 12px; margin-bottom: 20px; }
          .meta div { margin-bottom: 3px; }
          .meta b { display: inline-block; min-width: 150px; }
          .programme { font-size: 12pt; font-weight: bold; text-transform: uppercase; margin: 0 0 16px; }
          .date { font-weight: normal; font-size: 10pt; color: #444; }
          table { width: 100%; border-collapse: collapse; margin: 6px 0 10px; }
          th, td { border: 1px solid #000; padding: 6px 8px; font-size: 10pt; text-align: left; vertical-align: top; }
          th { background: #eee; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Appendage to request for CPD allocation</h1>

        <div class="meta">
          <div><b>Chapter</b> ${esc(req.chapter?.name ?? "—")}</div>
          <div><b>Venue</b> ${esc(req.location)}</div>
          <div><b>Dates</b> ${esc(fmtDay(req.start_date, 0))}${
            req.end_date && req.end_date !== req.start_date ? ` – ${esc(fmtDay(req.end_date, 0))}` : ""
          }</div>
          <div><b>CPD points requested</b> ${esc(String(req.proposed_cpd_points))}${
            req.approved_cpd_points != null ? ` &nbsp;·&nbsp; <b style="min-width:0">approved</b> ${esc(String(req.approved_cpd_points))}` : ""
          }</div>
        </div>

        <div class="programme">Title / name of programme: ${esc(req.title)}</div>
        ${req.theme ? `<p><i>Theme: ${esc(req.theme)}</i></p>` : ""}

        <h2>${SECTIONS.introduction.no} ${SECTIONS.introduction.title}</h2>
        ${paras(doc.introduction)}

        <h2>${SECTIONS.aims_objectives.no} ${SECTIONS.aims_objectives.title}</h2>
        ${paras(doc.aims_objectives)}

        <h2>${SECTIONS.about_theme.no} ${SECTIONS.about_theme.title}</h2>
        ${paras(doc.about_theme)}

        ${days}

        ${doc.other_information.trim()
          ? `<h2>${SECTIONS.other_information.title}</h2>${paras(doc.other_information)}`
          : ""}
      </body>
    </html>
  `);

  w.document.close();
  w.focus();
  // The window has to have laid out before it is printed, or Chrome prints an
  // empty sheet. Same wait the logbook diet export uses.
  setTimeout(() => w.print(), 400);
}

// ── read-only ────────────────────────────────────────────────────────────────

function ReadSection({ no, title, body }: { no: string; title: string; body: string }) {
  return (
    <div>
      <div className="text-xs font-bold text-gray-900">
        {no ? <span className="text-gray-400 mr-1.5">{no}</span> : null}
        {title}
      </div>
      {body.trim() ? (
        <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{body}</p>
      ) : (
        <p className="text-sm text-gray-400 italic mt-1">Not completed.</p>
      )}
    </div>
  );
}

/**
 * The appendage as submitted.
 *
 * What the secretariat reads before it sets the points, and what the chairman
 * sees once his request has been ruled on and the document has locked.
 */
export function AppendageView({
  req,
  defaultOpen = true,
}: {
  req: ChapterEventRequestDetail;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const doc = req.appendage ?? EMPTY_APPENDAGE;
  const complete = !!req.appendage_complete;

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 bg-gray-50 border-b">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-left min-w-0"
        >
          {open ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
          <FileText className="w-4 h-4 text-gray-400 shrink-0" />
          <span className="font-bold text-gray-900 text-sm truncate">Appendage to the request</span>
          <AppendageChip complete={complete} />
        </button>

        <button
          type="button"
          onClick={() => printAppendage(req)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold text-gray-700 hover:bg-white shrink-0"
        >
          <Printer className="w-3.5 h-3.5" /> Print
        </button>
      </div>

      {open && (
        <div className="p-4 sm:p-5 space-y-5">
          {!complete && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                The chapter has not completed the appendage. The points are being asked
                for without the programme behind them.
              </span>
            </div>
          )}

          <ReadSection no={SECTIONS.introduction.no} title={SECTIONS.introduction.title} body={doc.introduction} />
          <ReadSection no={SECTIONS.aims_objectives.no} title={SECTIONS.aims_objectives.title} body={doc.aims_objectives} />
          <ReadSection no={SECTIONS.about_theme.no} title={SECTIONS.about_theme.title} body={doc.about_theme} />

          {doc.days.map((day, i) => (
            <div key={day.date + i} className="border-t pt-4 space-y-3">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-xs font-black text-gray-900">
                  <span className="text-gray-400 mr-1.5">{dayNo(i)}</span>Day {i + 1} programme
                </span>
                <span className="text-[11px] text-gray-500">{fmtDay(day.date, i)}</span>
                {dayIsEmpty(day) && (
                  <span className="text-[10px] font-bold uppercase tracking-wide text-amber-600">Nothing entered</span>
                )}
              </div>

              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {day.programme.trim() || <span className="text-gray-400 italic">Not completed.</span>}
              </p>

              <ReadSection no={`${dayNo(i)}.1`} title="Theme / subthemes / training session planned" body={day.sessions} />
              <ReadSection no={`${dayNo(i)}.2`} title="Technical papers" body={day.technical_papers} />

              <div>
                <div className="text-xs font-bold text-gray-900">
                  <span className="text-gray-400 mr-1.5">{dayNo(i)}.3</span>
                  Resource people for keynote / subthemes / technical papers
                </div>

                {day.resource_persons.length === 0 ? (
                  <p className="text-sm text-gray-400 italic mt-1">None listed.</p>
                ) : (
                  <div className="overflow-x-auto mt-2 border rounded-xl">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-3 py-2 text-left font-bold w-10">S/N</th>
                          <th className="px-3 py-2 text-left font-bold">Sub-theme / technical paper</th>
                          <th className="px-3 py-2 text-left font-bold">Resource person</th>
                          <th className="px-3 py-2 text-left font-bold">Profile</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {day.resource_persons.map((p, idx) => (
                          <tr key={idx} className="align-top">
                            <td className="px-3 py-2 text-gray-400">{idx + 1}.</td>
                            <td className="px-3 py-2 text-gray-700">{p.topic || "—"}</td>
                            <td className="px-3 py-2 font-semibold text-gray-900">{p.person || "—"}</td>
                            <td className="px-3 py-2 text-gray-600 whitespace-pre-wrap">{p.profile || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ))}

          {!!doc.other_information.trim() && (
            <div className="border-t pt-4">
              <ReadSection no="" title={SECTIONS.other_information.title} body={doc.other_information} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── the form ─────────────────────────────────────────────────────────────────

function Field({
  no, title, hint, rows, value, onChange,
}: {
  no: string; title: string; hint: string; rows: number;
  value: string; onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block">
        <span className="text-xs font-bold text-gray-900">
          {no ? <span className="text-gray-400 mr-1.5">{no}</span> : null}
          {title}
        </span>
        <span className="block text-[11px] text-gray-400 mt-0.5 leading-snug">{hint}</span>
      </label>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
      />
    </div>
  );
}

/**
 * The chairman's copy of the template.
 *
 * Open for exactly as long as the request itself is — while it is `pending`, and
 * again if it comes back `rejected`, which is when a chapter refused for a thin
 * appendage is amending this very document. Saving it goes through the ordinary
 * request update, so a rejected request re-enters the queue on save rather than
 * being quietly amended after a decision.
 */
export function AppendageForm({
  req,
  onSaved,
}: {
  req: ChapterEventRequestDetail;
  onSaved: (message: string) => void;
}) {
  const [doc, setDoc] = useState<ChapterEventAppendage>(() => withBlankRows(req.appendage ?? EMPTY_APPENDAGE));
  const [error, setError] = useState("");
  const [save, { isLoading }] = useUpdateChapterEventRequestMutation();

  // The days belong to the event's dates, not to this form. Editing the request
  // above — a day added, a day dropped, the whole thing moved a week — re-sends
  // them, and what was typed for day 1 stays with day 1. Keyed on the dates
  // themselves rather than on the count, so moving a one-day event re-labels it.
  const daySignature = (req.appendage?.days ?? []).map((d) => d.date).join(",");

  useEffect(() => {
    setDoc((current) => ({
      ...current,
      days: (req.appendage?.days ?? []).map((server, i) =>
        current.days[i]
          ? { ...current.days[i], date: server.date }
          : { ...server, resource_persons: [{ ...BLANK_PERSON }] },
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daySignature]);

  const set = <K extends keyof ChapterEventAppendage>(k: K, v: ChapterEventAppendage[K]) =>
    setDoc((d) => ({ ...d, [k]: v }));

  const setDay = (i: number, patch: Partial<ChapterEventAppendageDay>) =>
    setDoc((d) => ({
      ...d,
      days: d.days.map((day, idx) => (idx === i ? { ...day, ...patch } : day)),
    }));

  const setPerson = (dayIdx: number, rowIdx: number, patch: Partial<typeof BLANK_PERSON>) =>
    setDoc((d) => ({
      ...d,
      days: d.days.map((day, idx) =>
        idx !== dayIdx
          ? day
          : {
              ...day,
              resource_persons: day.resource_persons.map((p, r) => (r === rowIdx ? { ...p, ...patch } : p)),
            },
      ),
    }));

  const addPerson = (dayIdx: number) =>
    setDoc((d) => ({
      ...d,
      days: d.days.map((day, idx) =>
        idx === dayIdx ? { ...day, resource_persons: [...day.resource_persons, { ...BLANK_PERSON }] } : day,
      ),
    }));

  const dropPerson = (dayIdx: number, rowIdx: number) =>
    setDoc((d) => ({
      ...d,
      days: d.days.map((day, idx) =>
        idx !== dayIdx
          ? day
          : {
              ...day,
              // Never down to nothing — the table has to keep a row to type into.
              resource_persons:
                day.resource_persons.length > 1
                  ? day.resource_persons.filter((_, r) => r !== rowIdx)
                  : [{ ...BLANK_PERSON }],
            },
      ),
    }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      // Only the appendage. Sending the whole request back would overwrite the
      // title or the dates with whatever this form was holding when it loaded.
      const res: any = await save({ id: req.id, body: { appendage: doc } }).unwrap();
      onSaved(res?.message || "Appendage saved.");
    } catch (err) {
      setError(safeMsg(err));
    }
  };

  const noDates = doc.days.length === 0;

  return (
    <form onSubmit={submit} className="border border-gray-200 rounded-2xl overflow-hidden">
      <div className="px-4 sm:px-5 py-3 bg-gray-50 border-b flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 text-gray-400 shrink-0" />
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 text-sm">Appendage to the request</h3>
            <p className="text-[11px] text-gray-500">
              The programme behind the points — what the secretariat judges the allocation on.
            </p>
          </div>
        </div>
        <AppendageChip complete={req.appendage_complete} />
      </div>

      <div className="p-4 sm:p-5 space-y-5">
        {!!error && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {req.status === "rejected" && (
          <p className="text-xs text-gray-500">
            Saving the appendage sends the request back to the secretariat for a fresh decision.
          </p>
        )}

        <Field
          {...SECTIONS.introduction}
          rows={4}
          value={doc.introduction}
          onChange={(v) => set("introduction", v)}
        />
        <Field
          {...SECTIONS.aims_objectives}
          rows={4}
          value={doc.aims_objectives}
          onChange={(v) => set("aims_objectives", v)}
        />
        <Field
          {...SECTIONS.about_theme}
          rows={4}
          value={doc.about_theme}
          onChange={(v) => set("about_theme", v)}
        />

        {noDates && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>Set the event's start and end dates above, and a block will appear here for each day.</span>
          </div>
        )}

        {doc.days.map((day, i) => (
          <div key={i} className="border-t pt-5 space-y-4">
            <div className="flex flex-wrap items-baseline gap-2">
              <h4 className="text-sm font-black text-gray-900">
                <span className="text-gray-400 mr-1.5">{dayNo(i)}</span>Day {i + 1} programme
              </h4>
              <span className="text-[11px] text-gray-500">{fmtDay(day.date, i)}</span>
            </div>

            <div className="space-y-1.5">
              <span className="block text-[11px] text-gray-400 leading-snug">
                A brief summary of the programme for this day.
              </span>
              <textarea
                rows={3}
                value={day.programme}
                onChange={(e) => setDay(i, { programme: e.target.value })}
                className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500"
              />
            </div>

            {DAY_FIELDS.map((f) => (
              <Field
                key={f.key}
                no={`${dayNo(i)}${f.sub}`}
                title={f.title}
                hint={f.hint}
                rows={f.rows}
                value={day[f.key]}
                onChange={(v) => setDay(i, { [f.key]: v } as Partial<ChapterEventAppendageDay>)}
              />
            ))}

            <div className="space-y-2">
              <div>
                <span className="text-xs font-bold text-gray-900">
                  <span className="text-gray-400 mr-1.5">{dayNo(i)}.3</span>
                  Resource people for keynote / subthemes / technical papers
                </span>
                <span className="block text-[11px] text-gray-400 mt-0.5 leading-snug">
                  One row per paper. Rows left blank are not saved.
                </span>
              </div>

              <div className="space-y-2">
                {day.resource_persons.map((p, r) => (
                  <div key={r} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start border rounded-xl p-2.5">
                    <div className="sm:col-span-1 pt-2.5 text-xs font-bold text-gray-400">{r + 1}.</div>
                    <input
                      value={p.topic}
                      onChange={(e) => setPerson(i, r, { topic: e.target.value })}
                      placeholder="Sub-theme / technical paper"
                      className="sm:col-span-4 w-full border px-3 py-2 rounded-lg text-sm outline-none focus:border-blue-500"
                    />
                    <input
                      value={p.person}
                      onChange={(e) => setPerson(i, r, { person: e.target.value })}
                      placeholder="Resource person"
                      className="sm:col-span-3 w-full border px-3 py-2 rounded-lg text-sm outline-none focus:border-blue-500"
                    />
                    <textarea
                      rows={2}
                      value={p.profile}
                      onChange={(e) => setPerson(i, r, { profile: e.target.value })}
                      placeholder="Profile"
                      className="sm:col-span-3 w-full border px-3 py-2 rounded-lg text-sm outline-none focus:border-blue-500"
                    />
                    <div className="sm:col-span-1 flex sm:justify-end">
                      <button
                        type="button"
                        onClick={() => dropPerson(i, r)}
                        title="Remove this row"
                        className="p-2 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => addPerson(i)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-semibold text-gray-700 hover:bg-gray-50"
              >
                <Plus className="w-3.5 h-3.5" /> Add resource person
              </button>
            </div>
          </div>
        ))}

        <div className="border-t pt-5">
          <Field
            {...SECTIONS.other_information}
            rows={3}
            value={doc.other_information}
            onChange={(v) => set("other_information", v)}
          />
        </div>
      </div>

      <div className="px-4 sm:px-5 py-3 border-t bg-gray-50 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-40"
        >
          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save appendage
        </button>
        <button
          type="button"
          onClick={() => printAppendage({ ...req, appendage: doc })}
          className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg border text-xs font-semibold text-gray-700 hover:bg-white"
        >
          <Printer className="w-3.5 h-3.5" /> Print
        </button>
        <span className="text-[11px] text-gray-400">
          You can save it as often as you like until the secretariat rules on the request.
        </span>
      </div>
    </form>
  );
}

/**
 * The document with somewhere to type.
 *
 * The API drops blank resource-person rows on save, which is right for what is
 * stored and wrong for what is shown: a day that came back with none would
 * offer no row at all. So the form adds one back on the way in, and the API
 * takes it out again on the way out.
 */
function withBlankRows(doc: ChapterEventAppendage): ChapterEventAppendage {
  return {
    ...EMPTY_APPENDAGE,
    ...doc,
    days: (doc.days ?? []).map((d) => ({
      ...d,
      resource_persons: d.resource_persons?.length ? d.resource_persons : [{ ...BLANK_PERSON }],
    })),
  };
}
